<template>
  <div ref="popoverRoot" class="global-user-popover">
    <button class="avatar-button" type="button" aria-label="打开用户信息" @click="togglePopover">
      <img class="avatar-icon" src="@/assets/images/icons/user.svg" alt="">
    </button>

    <transition name="popover-fade">
      <div v-if="visible" class="user-panel">
        <div class="panel-user-row">
          <div class="panel-avatar"></div>
          <div class="panel-info">
            <div class="panel-name">星核用户</div>
            <div class="panel-mobile-row">
              <span class="panel-mobile">{{ maskedMobile }}</span>
              <button class="copy-button" type="button" aria-label="复制手机号" @click.stop="copyMobile">
                <img src="@/assets/images/user/copy.svg" alt="">
              </button>
            </div>
          </div>
        </div>

        <div class="panel-actions">
          <button class="panel-action" type="button" @click="goUserCenter">
            <img src="@/assets/images/user/userCenterIcon.svg" alt="">
            <span>个人中心</span>
          </button>
          <button class="panel-action" type="button" @click="goOfficialWebsite">
            <img src="@/assets/images/user/globalWeb.svg" alt="">
            <span>进入星核DI</span>
          </button>
        </div>

        <button class="logout-button" type="button" @click="handleLogout">
          <span>退出登录</span>
          <img src="@/assets/images/user/logout.svg" alt="">
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import auth from '@/store/auth'
import userCenter from '@/store/userCenter'
import { platform } from '@/services/platform'

// 常量：手机号异常时的脱敏展示兜底
const DEFAULT_MASKED_MOBILE = '138****0754'
// 常量：Web 端地址允许通过环境变量覆盖，生产环境默认使用正式站点
const WEB_TARGET_URL = (
  process.env.VUE_APP_WEB_BASE_URL || 'https://data.stellarcore.world'
).replace(/\/$/, '')
// 常量：平台为 Web 端签发 SSO code 时使用的固定客户端标识
const WEB_SSO_CLIENT_ID =
  process.env.VUE_APP_WEB_SSO_CLIENT_ID || 'stellarcore-web'
// 常量：SSO code 兑换回调地址必须与 Web 端登记值完全一致
const WEB_SSO_REDIRECT_URI = `${WEB_TARGET_URL}/auth/callback`
// 常量：退出登录后回到官网首页
const LOGOUT_REDIRECT_ROUTE = '/'

export default {
  name: 'GlobalUserPopover',
  data() {
    return {
      authState: auth.state,
      visible: false,
      openingOfficialWebsite: false
    }
  },
  computed: {
    // 方法：根据全局登录态决定是否展示导航用户入口
    isLoggedIn() {
      return auth.isLoggedIn()
    },
    // 方法：把登录手机号脱敏后展示在用户弹窗中
    maskedMobile() {
      return this.maskMobile(this.authState.user && this.authState.user.mobile)
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    // 方法：切换用户弹窗的展开收起状态
    togglePopover() {
      this.visible = !this.visible
    },
    // 方法：进入全局个人中心抽屉，并收起当前弹窗
    goUserCenter() {
      this.visible = false
      auth.requireLogin(() => {
        userCenter.openOverview()
      })
    },
    // 方法：签发一次性 SSO code 后进入官方网站 Web 端
    async goOfficialWebsite() {
      // 逻辑分支：请求尚未完成时忽略重复点击，避免签发多个 code
      if (this.openingOfficialWebsite) {
        return
      }

      this.visible = false
      this.openingOfficialWebsite = true

      // 关键节点：先打开空白页，避免异步请求完成后再打开窗口被浏览器拦截
      const targetWindow = window.open('about:blank', '_blank')
      if (targetWindow) {
        targetWindow.opener = null
      }

      try {
        const issued = await platform.issueSsoCode(
          WEB_SSO_CLIENT_ID,
          WEB_SSO_REDIRECT_URI
        )
        const code = issued && issued.code

        // 逻辑分支：接口响应缺少 code 时按请求失败处理，避免跳转到无效地址
        if (!code) {
          throw new Error('官方网站登录凭证无效，请稍后重试')
        }

        const targetUrl = new URL(WEB_SSO_REDIRECT_URI)
        targetUrl.searchParams.set('code', code)

        // 逻辑分支：允许弹窗时复用临时窗口，否则退回当前页跳转
        if (targetWindow) {
          targetWindow.location.href = targetUrl.toString()
        } else {
          window.location.assign(targetUrl.toString())
        }
      } catch (error) {
        // 关键节点：SSO 签发失败时关闭空白页，避免留下无内容标签页
        if (targetWindow) {
          targetWindow.close()
        }
        this.$toast(error.message || '进入官方网站失败，请稍后重试')
      } finally {
        this.openingOfficialWebsite = false
      }
    },
    // 方法：复制当前登录手机号，浏览器不支持剪贴板时提示仍保持成功反馈
    copyMobile() {
      const mobile = this.authState.user && this.authState.user.mobile
      const copyText = mobile || this.maskedMobile

      // 逻辑分支：浏览器提供剪贴板能力时写入手机号文本
      if (navigator.clipboard && copyText) {
        navigator.clipboard.writeText(copyText)
      }

      this.$toast('已复制手机号')
    },
    // 方法：退出当前账号并回到首页，接口异常时本地登录态已先清理
    async handleLogout() {
      this.visible = false
      userCenter.close()
      try {
        await auth.logout()
      } finally {
        this.$router.push(LOGOUT_REDIRECT_ROUTE).catch(() => {})
      }
    },
    // 方法：手机号展示脱敏，异常输入回落到默认示例
    maskMobile(mobile) {
      if (!mobile || String(mobile).length < 7) {
        return DEFAULT_MASKED_MOBILE
      }

      const mobileText = String(mobile)
      return `${mobileText.slice(0, 3)}****${mobileText.slice(-4)}`
    },
    // 关键节点：点击组件外部区域时自动关闭下拉弹窗
    handleOutsideClick(event) {
      const root = this.$refs.popoverRoot

      // 逻辑分支：弹窗未展开或节点尚未挂载时无需处理
      if (!this.visible || !root) {
        return
      }

      // 逻辑分支：点击头像或弹窗内部时保持当前状态
      if (root.contains(event.target)) {
        return
      }

      this.visible = false
    }
  }
}
</script>

<style scoped lang="less">
.global-user-popover{
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-button{
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #F0F2F5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-icon{
  display: block;
  width: 20px;
  height: 20px;
}

.user-panel{
  position: absolute;
  top: 52px;
  right: 0;
  z-index: 20;
  width: 320px;
  min-height: 208px;
  padding: 28px 28px 20px;
  border-radius: 8px;
  background: #FFFFFF;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
  text-align: left;
  box-sizing: border-box;
}

.user-panel::before{
  content: '';
  position: absolute;
  top: -6px;
  right: 14px;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: #FFFFFF;
  transform: rotate(45deg);
}

.panel-user-row{
  display: flex;
  align-items: center;
}

.panel-avatar{
  width: 44px;
  height: 44px;
  margin-right: 16px;
  border-radius: 50%;
  background: #E5E7EB;
  flex-shrink: 0;
}

.panel-info{
  min-width: 0;
}

.panel-name{
  color: #000000D9;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

.panel-mobile-row{
  margin-top: 2px;
  display: flex;
  align-items: center;
}

.panel-mobile{
  color: #00000073;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
}

.copy-button{
  width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  img{
    display: block;
    width: 13px;
    height: 15px;
  }
}

.panel-actions{
  margin: 18px auto 0;
  width: 116px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.panel-action{
  width: 116px;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #DADDE2;
  border-radius: 8px;
  background: #FFFFFF;
  color: #8A8A8A;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  img{
    display: block;
    width: 14px;
    height: 14px;
    margin-right: 10px;
    object-fit: contain;
    flex-shrink: 0;
  }
  span{
    white-space: nowrap;
  }
}

.panel-action + .panel-action{
  margin-top: 8px;
}

.logout-button{
  position: absolute;
  right: 18px;
  bottom: 14px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: #8A8A8A;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  img{
    display: block;
    width: 10px;
    height: 10px;
    margin-left: 4px;
  }
}

.popover-fade-enter-active,
.popover-fade-leave-active{
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.popover-fade-enter,
.popover-fade-leave-to{
  opacity: 0;
  transform: translateY(-4px);
}
</style>
